'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuthContext } from '@/context/AuthContext';
import { useConfigsContext } from '@/context/ConfigsContext';
import { useUAContext } from '@/context/UAContext';
import { enableFcm } from '@/lib/configs';
import { disableWebPushNotification, hasRegisteredFcmDevice, registerWebPushNotification, subscribeToForegroundMessages } from '@/lib/fcm';
import { useLocale } from 'next-intl';
import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
type FcmContextType = {
  token: string | null;
};
const FcmContext = createContext<FcmContextType | null>(null);
type FcmProviderProps = PropsWithChildren;
export const FcmProvider = t0 => {
  const $ = _c(15);
  const {
    children
  } = t0;
  const [token, setToken] = useState(null);
  const locale = useLocale();
  const {
    isMobile
  } = useUAContext();
  const {
    appConfig
  } = useConfigsContext();
  const {
    htcAuthKey
  } = useAuthContext();
  let t1;
  if ($[0] !== appConfig?.links) {
    t1 = appConfig?.links || {};
    $[0] = appConfig?.links;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    viverse_landing
  } = t1;
  let t2;
  if ($[2] !== viverse_landing) {
    t2 = viverse_landing?.replace(/\/$/, "");
    $[2] = viverse_landing;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const viverseDomain = t2;
  let t3;
  let t4;
  if ($[4] !== htcAuthKey || $[5] !== isMobile || $[6] !== locale || $[7] !== viverseDomain) {
    t3 = () => {
      if (typeof window === "undefined" || typeof Notification === "undefined") {
        return;
      }
      if (isMobile) {
        setToken(null);
        return;
      }
      if (!enableFcm()) {
        setToken(null);
        disableWebPushNotification(htcAuthKey);
        return;
      }
      if (!htcAuthKey) {
        return;
      }
      let unsubscribe = null;
      let permissionStatus = null;
      if (Notification.permission === "denied") {
        if (hasRegisteredFcmDevice()) {
          disableWebPushNotification(htcAuthKey);
        }
        return;
      }
      const handlePermissionChange = () => {
        if (permissionStatus?.state === "denied") {
          unsubscribe?.();
          unsubscribe = null;
          setToken(null);
          disableWebPushNotification(htcAuthKey);
        }
      };
      navigator.permissions?.query({
        name: "notifications"
      }).then(status => {
        permissionStatus = status;
        status.addEventListener("change", handlePermissionChange);
      });
      registerWebPushNotification(htcAuthKey, locale).then(result => {
        if (!result) {
          return;
        }
        setToken(result.fcmToken);
        unsubscribe = subscribeToForegroundMessages(result.messaging, payload => {
          if (document.hidden) {
            return;
          }
          const title = payload.data?.title || "VIVERSE";
          const body = payload.data?.body || "";
          const url = payload.data?.url || viverseDomain || "https://www.viverse.com";
          try {
            const n = new Notification(title, {
              body
            });
            if (url) {
              n.onclick = () => window.open(url, "_blank");
            }
          } catch {}
        });
      });
      return () => {
        unsubscribe?.();
        permissionStatus?.removeEventListener("change", handlePermissionChange);
      };
    };
    t4 = [htcAuthKey, isMobile, locale, viverseDomain];
    $[4] = htcAuthKey;
    $[5] = isMobile;
    $[6] = locale;
    $[7] = viverseDomain;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  let t5;
  if ($[10] !== token) {
    t5 = {
      token
    };
    $[10] = token;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== children || $[13] !== t5) {
    t6 = <FcmContext.Provider value={t5}>{children}</FcmContext.Provider>;
    $[12] = children;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};
export const useFcmContext = () => {
  const value = useContext(FcmContext);
  if (value == null) {
    throw new Error("useFcmContext cannot be used outside of FcmProvider");
  }
  return value;
};